import { memo, useCallback, useContext, useMemo, useRef } from 'react';
import { Text } from 'foundations-components/components/Text';
import { AvatarWrapper } from '../shared/AvatarWrapper';
import { BASIC, ADVANCED } from '../../types/TranscriptTypes';
import { secondsToFormattedTime } from '../../utils/dateTimeFormatUtils';
import { MatchPopover } from '../participantMatching/MatchPopover';
import { getSpeakerInfo } from '../../utils/transcriptUtils';
import { TranscriptContext } from '../../contexts/TranscriptContext';
import { MODEL_INFERRED } from '../../types/MappedCategoryTypes';
import { AIIcon } from '../participantMatching/AIIcon';
import { getParticipantColor } from '../../utils/getParticipantColor';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { View } from 'foundations-components/components/View';
import { SelectableBox } from 'foundations-components/transitional/components/SelectableBox';
import UtteranceActionButtons from './UtteranceActionButtons';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { MediaPlaybackControlsContext } from '../../media-player/context/MediaPlaybackControlsContext';
import { CommentViewerButton } from './CommentViewerButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MemoizedTranscriptUtterance = /*#__PURE__*/memo(({
  utteranceData,
  utteranceId,
  isSelected,
  showAIIcon,
  onShareIconClick,
  onCommentIconClick,
  onCopyIconClick,
  renderCommentButton,
  onUtteranceClickCallback,
  utteranceScrollCallback,
  handleSetCurrentTime,
  isPaused,
  variant = ADVANCED,
  allComments,
  showCommentViewerButton,
  nextUtteranceStartTime
}) => {
  const popoverRef = useRef(null);
  const {
    transcriptParticipants,
    setAutoScrollTranscript,
    isMatchingPopoverOpen,
    setIsMatchingPopoverOpen
  } = useContext(TranscriptContext);
  const handleAvatarIconClick = useCallback(() => {
    if (!isMatchingPopoverOpen) {
      setIsMatchingPopoverOpen(true);
    }
  }, [isMatchingPopoverOpen, setIsMatchingPopoverOpen]);

  // get speaker data
  const speakerData = useMemo(() => getSpeakerInfo(utteranceData, transcriptParticipants), [transcriptParticipants, utteranceData]);
  const handleUtteranceClick = useCallback(() => {
    if (onUtteranceClickCallback) {
      onUtteranceClickCallback();
    }
    if (!isPaused) {
      setAutoScrollTranscript(true);
    }
    handleSetCurrentTime(utteranceData.startTimeSeconds);

    /* Since we do not have auto scroll for insights we pass in a
    scroll callback function to be called when an utterance is clicked in the
    Insights tab
    */
    if (utteranceScrollCallback) {
      utteranceScrollCallback(utteranceId);
    }
  }, [onUtteranceClickCallback, isPaused, handleSetCurrentTime, utteranceData.startTimeSeconds, utteranceScrollCallback, setAutoScrollTranscript, utteranceId]);
  const participantColor = useMemo(() => {
    const participantIndex = utteranceData.speakerTrackId - 1 > 0 ? utteranceData.speakerTrackId - 1 : 0;
    return getParticipantColor(participantIndex);
  }, [utteranceData.speakerTrackId]);
  return /*#__PURE__*/_jsx(View, {
    marginBlockEnd: "300",
    marginInlineEnd: "200",
    id: utteranceId,
    position: "relative",
    "data-test-id": "transcriptUtteranceContainerParent",
    children: /*#__PURE__*/_jsxs(SelectableBox, {
      type: "default",
      block: true,
      "data-test-id": "transcriptUtteranceContainer",
      onClick: handleUtteranceClick,
      selected: isSelected,
      padding: "400",
      paddingBlockStart: "300",
      textAlign: "left",
      children: [/*#__PURE__*/_jsx(MediaObject, {
        itemLeft: /*#__PURE__*/_jsx(MatchPopover, {
          disabled: variant === BASIC,
          speaker: speakerData,
          matchIconId: utteranceId,
          speakerTrackId: utteranceData.speakerTrackId,
          children: /*#__PURE__*/_jsx(AvatarWrapper, {
            ref: popoverRef,
            onClick: handleAvatarIconClick,
            objectId: speakerData.associatedObjectId,
            email: speakerData.participantEmail,
            displayName: speakerData.participantName,
            objectType: speakerData.participantType,
            size: "sm",
            marginBlockStart: "100",
            backgroundColor: participantColor
          })
        }),
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "0",
          children: [/*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(TruncateString, {
              useFlex: true,
              fixedChildren: /*#__PURE__*/_jsxs(_Fragment, {
                children: [showAIIcon && speakerData.mappedCategory === MODEL_INFERRED && /*#__PURE__*/_jsx(AIIcon, {}), showCommentViewerButton && allComments && /*#__PURE__*/_jsx("span", {
                  style: {
                    marginInlineStart: '8px'
                  },
                  onClick: e => e.stopPropagation(),
                  children: /*#__PURE__*/_jsx(CommentViewerButton, {
                    utteranceStartTime: utteranceData.startTimeSeconds,
                    nextUtteranceStartTime: nextUtteranceStartTime,
                    speakerName: speakerData.participantName,
                    allComments: allComments
                  })
                })]
              }),
              tooltip: speakerData.participantName,
              children: /*#__PURE__*/_jsx(Text, {
                as: "span",
                typography: "body-125",
                "data-test-id": "utterance-speakerName",
                children: speakerData.participantName
              })
            }), /*#__PURE__*/_jsx(Box, {
              flexGrow: 0,
              flexBasis: "110px",
              paddingBlockStart: "50",
              paddingInlineStart: "100",
              children: /*#__PURE__*/_jsx(UtteranceActionButtons, {
                utteranceData: utteranceData,
                onShareIconClick: onShareIconClick,
                onCommentIconClick: onCommentIconClick,
                onCopyIconClick: onCopyIconClick,
                renderCommentButton: renderCommentButton
              })
            })]
          }), /*#__PURE__*/_jsx(Text, {
            "data-test-id": "utteranceTimestamp",
            use: "help",
            as: "small",
            marginBlockStart: "-100",
            children: secondsToFormattedTime(utteranceData.startTimeSeconds)
          })]
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "small",
        marginBlockEnd: "0",
        "data-test-id": "utteranceText",
        children: utteranceData.utterance
      })]
    })
  });
});
export const TranscriptUtterance = props => {
  const {
    onPlayerHeadUpdate,
    isPaused
  } = useContext(MediaPlaybackControlsContext);
  return /*#__PURE__*/_jsx(MemoizedTranscriptUtterance, Object.assign({}, props, {
    handleSetCurrentTime: onPlayerHeadUpdate,
    isPaused: isPaused
  }));
};